<script setup lang="ts">
import { ref, onMounted } from 'vue';
import {
    Dialog,
    DialogScrollContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-vue-next';
import { storeOrdersApiClient } from '@api-client/store_orders';
import { toast } from '@/ui-plus/sonner';
import type { ResType } from '@api-client/typeUtils';

type UberDirectGetDeliveryResponseType = ResType<
    typeof storeOrdersApiClient.uber_direct_get_delivery[":storeId"]['$post']
>["data"];

const props = defineProps<{
    storeId: string;
    deliveryId: string;
}>();

defineEmits<{
    close: [];
}>();

const isLoading = ref(true);
const delivery = ref<UberDirectGetDeliveryResponseType | null>(null);

onMounted(async () => {
    try {
        const { data, error } = await (await storeOrdersApiClient.uber_direct_get_delivery[":storeId"].$post({
            param: { storeId: props.storeId },
            json: { delivery_id: props.deliveryId },
        })).json();

        if (error) {
            toast.error(error.message ?? 'Failed to fetch Uber Direct delivery');
            return;
        }
        delivery.value = data;
    } finally {
        isLoading.value = false;
    }
});
</script>

<template>
    <Dialog :open="true"
            @update:open="(v) => !v && $emit('close')">
        <DialogScrollContent class="max-w-2xl">
            <DialogHeader>
                <DialogTitle>Uber Direct Delivery</DialogTitle>
            </DialogHeader>

            <div v-if="isLoading"
                 class="flex items-center gap-2 text-sm text-muted-foreground p-4">
                <Loader2 class="h-4 w-4 animate-spin" />
                Loading delivery...
            </div>

            <pre v-else-if="delivery"
                 class="text-xs bg-muted p-3 rounded overflow-auto max-h-[70vh] whitespace-pre-wrap break-all">{{ JSON.stringify(delivery, null, 2) }}</pre>

            <div v-else
                 class="text-sm text-muted-foreground p-4">
                No data.
            </div>
        </DialogScrollContent>
    </Dialog>
</template>
